'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useBreadcrumbs } from '@/utils/providers/BreadcrumbProvider';

interface PageTitleProps {
	pageTitle: string;
	breadcrumbTrailing?: string;
	showBackIcon?: boolean;
	variant?: 'h1' | 'h2';
	backUrl?: string;
	fallbackRoute?: string;
	sx?: SxProps;
	testId?: string;
	component?: React.ElementType;
}

const styles = {
	font: {
		color: 'black.main',
	},
} as const satisfies Record<string, SxProps>;

const PageTitle = ({
	pageTitle,
	breadcrumbTrailing,
	showBackIcon = false,
	variant = 'h1',
	backUrl,
	fallbackRoute,
	sx,
	testId = 'page-title',
	component = 'h1',
}: Readonly<PageTitleProps>) => {
	const router = useRouter();

	const { setTrailingPath } = useBreadcrumbs();

	useEffect(() => {
		setTrailingPath(breadcrumbTrailing ?? pageTitle);
	}, [pageTitle, breadcrumbTrailing, setTrailingPath]);

	const handleBackClick = () => {
		if (backUrl) {
			router.push(backUrl);
			return;
		}

		if (window.history.length > 1) {
			router.back();
			return;
		}

		if (fallbackRoute) {
			router.push(fallbackRoute);
		}
	};

	const typography = (
		<Typography
			variant={variant}
			component={component}
			sx={showBackIcon ? null : { color: styles.font.color, ...sx }}
			data-testid={testId}
		>
			{pageTitle}
		</Typography>
	);

	return showBackIcon ? (
		<Box
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				color: styles.font.color,
				gap: { xs: '8px', md: '16px' },
				...sx,
			}}
		>
			<ArrowBackIcon onClick={handleBackClick} sx={{ cursor: 'pointer' }} data-testid="page-title-back-button" />

			{typography}
		</Box>
	) : (
		typography
	);
};

export default PageTitle;
